import type { ImageDTO } from "@shared/types";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { CopyUrlButton } from "./CopyUrlButton";
import { dimensionsLabel, formatBytes, formatDate, mimeLabel } from "../lib/format";

interface Props {
  image: ImageDTO | null;
  onClose: () => void;
  onRename: (img: ImageDTO) => void;
  onMove: (img: ImageDTO) => void;
  onDelete: (img: ImageDTO) => void;
}

export function ImagePreviewModal({ image, onClose, onRename, onMove, onDelete }: Props) {
  if (!image) return null;

  const rows: [string, string][] = [
    ["Type", mimeLabel(image.mimeType)],
    ["Size", formatBytes(image.sizeBytes)],
    ["Dimensions", dimensionsLabel(image.width, image.height)],
    ["Folder", image.folderPath || "Uncategorized"],
    ["Created", formatDate(image.createdAt)],
    ["Updated", formatDate(image.updatedAt)],
  ];

  return (
    <Modal open={!!image} onClose={onClose} title={image.filename} size="xl">
      <div className="grid gap-4 md:grid-cols-[1.4fr_1fr] md:gap-5">
        <div className="checkerboard grid max-h-[40vh] place-items-center overflow-hidden rounded-xl border border-white/70 md:max-h-[65vh]">
          <img src={image.url} alt={image.filename} className="max-h-[40vh] w-full object-contain md:max-h-[65vh]" />
        </div>

        <div className="flex flex-col">
          <dl className="divide-y divide-zinc-900/10 text-[13px]">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-4 py-2">
                <dt className="flex-shrink-0 text-zinc-500">{k}</dt>
                <dd className="min-w-0 break-words text-right font-medium text-zinc-800" title={v}>
                  {v}
                </dd>
              </div>
            ))}
          </dl>

          <label className="mt-4 block text-2xs font-medium uppercase tracking-wide text-zinc-500">Direct URL</label>
          <input
            readOnly
            value={image.url}
            onFocus={(e) => e.currentTarget.select()}
            className="field mt-1.5 w-full font-mono text-xs"
          />

          <div className="mt-4 flex flex-wrap gap-2 pb-1">
            <CopyUrlButton url={image.url} size="md" variant="primary" />
            <a
              href={image.url}
              download={image.filename}
              className="glass inline-flex h-10 items-center rounded-lg px-4 text-sm font-medium text-zinc-800 transition-colors hover:bg-white/80"
            >
              Download
            </a>
            <Button size="md" onClick={() => onRename(image)}>
              Rename
            </Button>
            <Button size="md" onClick={() => onMove(image)}>
              Move
            </Button>
            <Button size="md" variant="danger" onClick={() => onDelete(image)}>
              Delete
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
